import React, { useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import { FieldArray, useField } from 'formik';
import Divider from '@material-ui/core/Divider';

import AddIcon from '@material-ui/icons/Add';
import { v4 as uuidv4 } from 'uuid';
import CueRow, { REMOVE_GUTTER } from './CueRow';
import { useRootTranslation } from '../../../../../../utils/hooks';
import { FormButton } from '../../../../Form/Fields';

const CueContainers = ({
  cueContainers,
  allCueContainers,
  cueContainersLoading,
  readOnly,
  clearanceId,
  trackId,
  qTrackId,
  refetch,
}) => {
  const t = useRootTranslation('drawers.clearanceUsesAndDurations');

  /* eslint-disable-next-line no-unused-vars */
  const [{ value: cues = [] }, _meta] = useField('cues');

  /** Group this clearance's live cues by container id in a single pass, keeping each cue's index
   * in the complete cue array. Avoids re-scanning the full cues array for every container on
   * every render (which is what made editing slow on tracks with many clearances/cues). */
  const cuesByContainerId = useMemo(() => {
    const grouped = new Map();
    cues.forEach((cue, index) => {
      if (cue.deleted || cue.qclearClearanceId !== clearanceId) return;
      cue.cueContainerIds.forEach((containerId) => {
        if (!grouped.has(containerId)) grouped.set(containerId, []);
        grouped.get(containerId).push({ ...cue, index });
      });
    });
    return grouped;
  }, [cues, clearanceId]);

  return cueContainers.map((container, containerIndex) => {
    const cuesInContainer = cuesByContainerId.get(container.id) ?? [];
    return (
      <Grid container item key={container.id}>
        {/** A different scene/asset gets a stronger, more-spaced divider than same-scene usages. */}
        {containerIndex > 0 && (
          <Grid container item>
            <Divider className="light" style={{ marginTop: '2.5em', marginBottom: '2.5em', height: 2 }} />
          </Grid>
        )}
        {cuesInContainer.map(({ id, index }, rowIndex) => (
          <React.Fragment key={id}>
            <CueRow
              name={`cues.${index}`}
              hideRemove={readOnly || cuesInContainer.length === 1}
              container={container}
              allCueContainers={allCueContainers}
              cueContainersLoading={cueContainersLoading}
              trackId={trackId}
              qTrackId={qTrackId}
              refetch={refetch}
            />
            {rowIndex < cuesInContainer.length - 1 && (
              <Grid container item style={{ paddingRight: REMOVE_GUTTER }}>
                <Divider className="light" style={{ marginTop: '1em', marginBottom: '1em' }} />
              </Grid>
            )}
          </React.Fragment>
        ))}
        {!readOnly && (
          <Grid container spacing={1} alignItems="center" style={{ marginTop: '1em' }}>
            <Grid item>
              <FieldArray name="cues">
                {(arrayHelpers) => (
                  <FormButton
                    alwaysEnabled
                    size="sm"
                    variant="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      arrayHelpers.push({
                        id: `newCue-${uuidv4()}`,
                        qclearClearanceId: clearanceId,
                        cueContainerIds: [container.id],
                      });
                      arrayHelpers.form.submitForm();
                    }}
                  >
                    {t('addUsage')}
                  </FormButton>
                )}
              </FieldArray>
            </Grid>
          </Grid>
        )}
      </Grid>
    );
  });
};

export default CueContainers;
